import { Stack, Typography, useTheme } from "@mui/material";
import { BellIcon, CheckCircleIcon, CircularLoader } from "components";
import { useUpdateCustomerNotificationsSettings } from "controller";
import {
  useUpdateMspResourceNotificationSettings,
  useUpdateMspUserNotificationSettings,
} from "controller/hooks/control-center/notifications";
import { useEffect, useState } from "react";
import { Control, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { debugConsole } from "utilz/helpers";

export const NotificationSoundChip = ({
  name,
  control,
  notifyMethod,
}: {
  name: string;
  control: Control<any>;
  notifyMethod: "notification_sound";
}) => {
  const theme = useTheme();
  const { id: customerId } = useParams();
  const ICON = (
    <BellIcon style={{ width: "18px", height: "18px" }} color="inherit" />
  );
  const LABEL = "Notification Sound";

  const { handleSubmit, watch } = useFormContext();
  const watchNotificationSound = watch(name);

  const [isNotificationSoundOn, setIsNotificationSoundOn] = useState(
    watchNotificationSound ?? false,
  );

  useEffect(() => {
    setIsNotificationSoundOn(watchNotificationSound || false);
  }, [watchNotificationSound]);

  const {
    mutateAsync: updateMspResourceNotificationSettings,
    isLoading: isUpdateMspResourceNotificationSettingsLoading,
  } = useUpdateMspResourceNotificationSettings();
  const {
    mutateAsync: updateMspUserNotificationSettings,
    isLoading: isUpdateMspUserNotificationSettingsLoading,
  } = useUpdateMspUserNotificationSettings();
  const {
    mutateAsync: updateCustomerNotificationsSettings,
    isLoading: isUpdateCustomerNotificationsSettingsLoading,
  } = useUpdateCustomerNotificationsSettings();

  const formFieldNameSplitArr = name.split(".");
  const notificationType =
    formFieldNameSplitArr[formFieldNameSplitArr.length - 2];
  const getFormDataForCurrentNotificationType = (formData: any) => {
    // if say, formFieldName = "resources.ticket.new_ticket.web_app" -> it gets value of formData.resources.ticket.new_ticket
    // this is done because - even though the drawer has only say, web_app's selected roles, the update api requires data of all mediums of the specific notification type - like email, mobile etc
    return formFieldNameSplitArr
      .slice(0, -1)
      .reduce((acc, key) => acc?.[key], formData);
  };

  const personnelType = name?.split(".")?.[0];
  const onSubmit = async (formData: any) => {
    const data = getFormDataForCurrentNotificationType(formData);
    debugConsole("submit data", data);
    const isNotificationSoundOnNew = !data?.notification_sound || false;
    debugConsole("submit isNotificationSoundOnNew", isNotificationSoundOnNew);
    try {
      const finalData = {
        notification_status:
          data?.web_app?.length > 0 ||
          data?.email?.length > 0 ||
          data?.resource_mobile_app?.length ||
          data?.agent_app_desktop?.length > 0 ||
          data?.agent_mobile_app?.length > 0 ||
          data?.teams_app?.length > 0,
        notification_sound: isNotificationSoundOnNew,
        notify_methods: [
          ...(data?.web_app?.length > 0
            ? [
                {
                  notify_through: "web_app",
                  roles: data?.web_app,
                },
              ]
            : []),
          ...(data?.email?.length > 0
            ? [
                {
                  notify_through: "email",
                  roles: data?.email,
                },
              ]
            : []),
          ...(data?.resource_mobile_app?.length > 0
            ? [
                {
                  notify_through: "resource_mobile_app",
                  roles: data?.resource_mobile_app,
                },
              ]
            : []),
          ...(data?.agent_app_desktop?.length > 0
            ? [
                {
                  notify_through: "agent_app_desktop",
                  roles: data?.agent_app_desktop,
                },
              ]
            : []),
          ...(data?.agent_mobile_app?.length > 0
            ? [
                {
                  notify_through: "agent_mobile_app",
                  roles: data?.agent_mobile_app,
                },
              ]
            : []),
          ...(data?.teams_app?.length > 0
            ? [
                {
                  notify_through: "teams_app",
                  roles: data?.teams_app,
                },
              ]
            : []),
        ],
      };
      debugConsole("submit finalData", finalData);
      setIsNotificationSoundOn(isNotificationSoundOnNew);
      data.notification_sound = isNotificationSoundOnNew;
      const resData =
        personnelType === "users"
          ? await updateMspUserNotificationSettings({
              data: finalData,
              notificationType,
            })
          : personnelType === "customers"
            ? await updateCustomerNotificationsSettings({
                data: finalData,
                customerId: customerId || "",
                notificationType,
              })
            : await updateMspResourceNotificationSettings({
                data: finalData,
                notificationType,
              });
      toast.success(
        `Notification sound has been turned ${
          isNotificationSoundOnNew ? "on" : "off"
        } successfully.`,
      );
    } catch (error) {
      const isNotificatonSoundOnOld = !isNotificationSoundOnNew;
      setIsNotificationSoundOn(isNotificatonSoundOnOld);
      toast.error(
        `Failed to turn ${
          isNotificatonSoundOnOld ? "on" : "off"
        } notification sound.`,
      );
    }
  };

  const onFormError = (error: any) => debugConsole("form submit error", error);

  const notificationTypePath = name.split(".").slice(0, -1).join("."); // eg: resources.project.new_project.notification_sound -> resources.project.new_project (i.e., popped out notification_sound from the string)
  const notificationData = watch(notificationTypePath);
  const getIsEnabled = () => {
    if (!notificationData) return false;
    return [
      "web_app",
      "email",
      "resource_mobile_app",
      "agent_app_desktop",
      "agent_mobile_app",
      "teams_app",
    ].some((field) => notificationData[field]?.length > 0);
  };
  const isEnabled = getIsEnabled();
  const isLoading =
    isUpdateMspResourceNotificationSettingsLoading ||
    isUpdateMspUserNotificationSettingsLoading ||
    isUpdateCustomerNotificationsSettingsLoading;
  const onChipClick = () => {
    isEnabled && !isLoading && handleSubmit(onSubmit, onFormError)();
  };
  return (
    <Stack direction="row" alignItems={"center"} gap="8px">
      <Stack
        direction="row"
        alignItems={"center"}
        gap="8px"
        sx={{
          padding: "8px 12px",
          cursor: isEnabled ? "pointer" : "not-allowed",
          transition: "all 0.3s",
          height: "32px",
          borderRadius: "32px",
          border: isNotificationSoundOn
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.neutral["010"]}`,
          backgroundColor: isNotificationSoundOn
            ? theme.palette.secondary["purple-tint-3"]
            : "#FFF",
          ":hover": {
            bgcolor: theme.palette.neutral["005"],
            color: theme.palette.lightBg.main,
            border: `1px solid ${theme.palette.neutral["015"]}`,
          },
          color: theme.palette.lightBg.main,
        }}
        onClick={onChipClick}
      >
        {ICON}
        <Typography
          variant="body-small"
          sx={{
            color: "inherit",
          }}
        >
          {LABEL}
        </Typography>
      </Stack>
      {isLoading && (
        <CircularLoader
          size="12px"
          sx={{
            "&.MuiCircularProgress-root": {
              color: theme.palette.neutral["020"],
            },
          }}
        />
      )}
    </Stack>
  );
};
